import type { ReportTableCell, ReportTableProps } from '@/components';
import type { projectDetailUrlTypeEnum } from '@/features/mold/models';
import {
  type ReportLocalGovernmentFundTableBModel,
  type ReportLocalGovernmentFundsTableBModel,
  reportLocalGovernmentFundTableBCsvHeader,
} from '@/features/mold/models/local-government-fund';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { FundNecessityTypeLabelModifier } from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import {
  FALLBACK_TEXT,
  convertToNegative,
  createCsv,
  formatAmountForPdf,
  formatAmountReport,
  formatNumber,
  validateTotalWithElements,
} from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../pdf/pdf-utils';

type TableBData = z.infer<typeof ReportLocalGovernmentFundTableBModel>;
type ConverterArgs = {
  data: TableBData;
  type: 'csv' | 'pdf';
  isDebugMode: boolean;
};

const valueConverter = ({ data, type, isDebugMode }: ConverterArgs) => {
  const { result: isNullBeginningOfTheFundBalance } = validateTotalWithElements(
    {
      totalValue: data.endingOfTheFundBalance,
      elements: [
        {
          value: data.beginningOfTheFundBalance,
          rawValue: data.rawBeginningOfTheFundBalance,
          negativeCount: data.negativeBeginningOfTheFundBalanceCount,
        },
        {
          value: data.incomeAmount,
          rawValue: data.rawIncomeAmount,
          negativeCount: data.negativeIncomeAmountCount,
        },
        {
          value: convertToNegative(data.businessExpense),
          rawValue: convertToNegative(data.rawBusinessExpense),
          negativeCount: data.negativeBusinessExpenseCount,
        },
        {
          value: convertToNegative(data.amountReturnedToTheNationalTreasury),
          rawValue: convertToNegative(
            data.rawAmountReturnedToTheNationalTreasury,
          ),
          negativeCount: data.negativeAmountReturnedToTheNationalTreasuryCount,
        },
      ],
    },
  );

  return {
    beginningOfTheFundBalance: formatAmountReport(
      data.beginningOfTheFundBalance,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeBeginningOfTheFundBalanceCount,
        rawValue: data.rawBeginningOfTheFundBalance,
      },
    ),
    nationalEquivalentOfBeginningOfTheFundBalance: formatAmountReport(
      data.nationalEquivalentOfBeginningOfTheFundBalance,
      type,
      {
        isDebugMode,
        negativeCount:
          data.negativeNationalEquivalentOfBeginningOfTheFundBalanceCount,
        rawValue: data.rawNationalEquivalentOfBeginningOfTheFundBalance,
      },
    ),
    incomeAmount: formatAmountReport(data.incomeAmount, type, {
      isDebugMode,
      negativeCount: data.negativeIncomeAmountCount,
      rawValue: data.rawIncomeAmount,
    }),
    nationalEquivalentOfIncomeAmount: formatAmountReport(
      data.nationalEquivalentOfIncomeAmount,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeNationalEquivalentOfIncomeAmountCount,
        rawValue: data.rawNationalEquivalentOfIncomeAmount,
      },
    ),
    grantedAmountByTheCountryFromInitial: formatAmountReport(
      data.grantedAmountByTheCountryFromInitial,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeGrantedAmountByTheCountryFromInitialCount,
        rawValue: data.rawGrantedAmountByTheCountryFromInitial,
      },
    ),
    grantedAmountByTheCountryFromSupplementary: formatAmountReport(
      data.grantedAmountByTheCountryFromSupplementary,
      type,
      {
        isDebugMode,
        negativeCount:
          data.negativeGrantedAmountByTheCountryFromSupplementaryCount,
        rawValue: data.rawGrantedAmountByTheCountryFromSupplementary,
      },
    ),
    grantedAmountByTheCountryFromReserve: formatAmountReport(
      data.grantedAmountByTheCountryFromReserve,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeGrantedAmountByTheCountryFromReserveCount,
        rawValue: data.rawGrantedAmountByTheCountryFromReserve,
      },
    ),
    otherIncomeAmount: formatAmountReport(data.otherIncomeAmount, type, {
      isDebugMode,
      negativeCount: data.negativeOtherIncomeAmountCount,
      rawValue: data.rawOtherIncomeAmount,
    }),
    businessExpense: formatAmountReport(data.businessExpense, type, {
      isDebugMode,
      negativeCount: data.negativeBusinessExpenseCount,
      rawValue: data.rawBusinessExpense,
    }),
    amountReturnedToTheNationalTreasury: formatAmountReport(
      data.amountReturnedToTheNationalTreasury,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeAmountReturnedToTheNationalTreasuryCount,
        rawValue: data.rawAmountReturnedToTheNationalTreasury,
      },
    ),
    // 当年度末基金残高
    endingOfTheFundBalance: formatAmountReport(
      data.endingOfTheFundBalance,
      type,
      {
        isDebugMode,
        negativeCount:
          isNullBeginningOfTheFundBalance === 'isNull'
            ? 1
            : data.negativeEndingOfTheFundBalanceCount,
        rawValue: data.rawEndingOfTheFundBalance,
      },
    ),
    nationalEquivalentOfTheEndOfTheFundBalance: formatAmountReport(
      data.nationalEquivalentOfTheEndOfTheFundBalance,
      type,
      {
        isDebugMode,
        negativeCount:
          data.negativeNationalEquivalentOfTheEndOfTheFundBalanceCount,
        rawValue: data.rawNationalEquivalentOfTheEndOfTheFundBalance,
      },
    ),
    decidedAmountOfSubsidy: formatAmountReport(
      data.decidedAmountOfSubsidy,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeDecidedAmountOfSubsidyCount,
        rawValue: data.rawDecidedAmountOfSubsidy,
      },
    ),
    decidedAmountOfInvestment: formatAmountReport(
      data.decidedAmountOfInvestment,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeDecidedAmountOfInvestmentCount,
        rawValue: data.rawDecidedAmountOfInvestment,
      },
    ),
    decidedAmountOfLoan: formatAmountReport(data.decidedAmountOfLoan, type, {
      isDebugMode,
      negativeCount: data.negativeDecidedAmountOfLoanCount,
      rawValue: data.rawDecidedAmountOfLoan,
    }),
    decidedAmountOfDebtGuarantee: formatAmountReport(
      data.decidedAmountOfDebtGuarantee,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeDecidedAmountOfDebtGuaranteeCount,
        rawValue: data.rawDecidedAmountOfDebtGuarantee,
      },
    ),
    decidedAmountOfOthers: formatAmountReport(
      data.decidedAmountOfOthers,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeDecidedAmountOfOthersCount,
        rawValue: data.rawDecidedAmountOfOthers,
      },
    ),
    balanceAmountOfInvestment: formatAmountReport(
      data.balanceAmountOfInvestment,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeBalanceAmountOfInvestmentCount,
        rawValue: data.rawBalanceAmountOfInvestment,
      },
    ),
    balanceAmountOfLoan: formatAmountReport(data.balanceAmountOfLoan, type, {
      isDebugMode,
      negativeCount: data.negativeBalanceAmountOfLoanCount,
      rawValue: data.rawBalanceAmountOfLoan,
    }),
    balanceAmountOfDebtGuarantee: formatAmountReport(
      data.balanceAmountOfDebtGuarantee,
      type,
      {
        isDebugMode,
        negativeCount: data.negativeBalanceAmountOfDebtGuaranteeCount,
        rawValue: data.rawBalanceAmountOfDebtGuarantee,
      },
    ),
  };
};

const getReportTargetYear = (fiscalYear: number) => {
  return fiscalYear - 1;
};

/// ////////////////////////////////////////
/// CSV
/// ////////////////////////////////////////

export const getLocalGovernmentFundTableBCsvHeader = (
  fiscalYear: number,
): Record<z.infer<typeof reportLocalGovernmentFundTableBCsvHeader>, string> => {
  const reportYear = getReportTargetYear(fiscalYear);

  return {
    fundSheetNumber: t('report-local-government-fund-header-fund-sheet-number'),
    ministryName: t('ministry'),
    projectName: t('fund-name'),
    beginningOfTheFundBalance: t(
      'report-local-government-fund-header-beginning-of-the-fund-balance',
      { year: reportYear - 1 },
    ),
    nationalEquivalentOfBeginningOfTheFundBalance: `${t(
      'report-local-government-fund-header-fund-balance',
      { year: reportYear - 1 },
    )}${t('amount-equivalent-to-national-expense')}`,
    incomeAmount: `${t('fiscal-year-with-value', { year: reportYear })}${t('report-local-government-fund-header-income')}`,
    nationalEquivalentOfIncomeAmount: `${t('fiscal-year-with-value', { year: reportYear })}${t('income')}${t('amount-equivalent-to-national-expense')}`,
    grantedAmountByTheCountryFromInitial: `${t('fiscal-year-with-value', { year: reportYear })}${t(
      'amount-of-funding-from-the-government',
    )}（${t('initial')}）`,
    grantedAmountByTheCountryFromSupplementary: `${t('fiscal-year-with-value', { year: reportYear })}${t(
      'amount-of-funding-from-the-government',
    )}（${t('correction')}）`,
    grantedAmountByTheCountryFromReserve: `${t('fiscal-year-with-value', { year: reportYear })}${t(
      'amount-of-funding-from-the-government',
    )}（${t('reserve-budget')}）`,
    accountingTypeNames: `${t('fiscal-year-with-value', { year: reportYear })}${t(
      'amount-of-funding-from-the-government',
    )}（${t('accounting-type')}）`,
    otherIncomeAmount: `${t('fiscal-year-with-value', { year: reportYear })}${t('income')}${t('others')}`,
    businessExpense: `${t('fiscal-year-with-value', { year: reportYear })}${t('report-local-government-fund-header-expense')}`,
    amountReturnedToTheNationalTreasury: t(
      'report-local-government-fund-header-amount-of-national-treasury-return',
      { year: reportYear },
    ),
    endingOfTheFundBalance: t(
      'report-local-government-fund-header-ending-of-the-fund-balance',
      { year: reportYear },
    ),
    nationalEquivalentOfTheEndOfTheFundBalance: `${t(
      'report-local-government-fund-header-fund-balance',
      { year: reportYear },
    )}${t('amount-equivalent-to-national-expense')}`,
    decidedNumberOfSubsidy: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-subsidy')}${t('report-local-government-fund-header-supplementary-for-local-government-fund-business')}（${t('count')}）`,
    decidedAmountOfSubsidy: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-subsidy')}${t('report-local-government-fund-header-supplementary-for-local-government-fund-business')}（${t('amount')}）`,
    decidedNumberOfInvestment: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('investment')}（${t('count')}）`,
    decidedAmountOfInvestment: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('investment')}（${t('amount')}）`,
    decidedNumberOfLoan: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('loan')}（${t('count')}）`,
    decidedAmountOfLoan: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('loan')}（${t('amount')}）`,
    decidedNumberOfDebtGuarantee: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('debt-guarantee')}（${t('count')}）`,
    decidedAmountOfDebtGuarantee: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('debt-guarantee')}（${t('amount')}）`,
    decidedNumberOfOthers: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-for-local-government-others')}（${t('count')}）`,
    decidedAmountOfOthers: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-for-local-government-others')}（${t('amount')}）`,
    balanceNumberOfInvestment: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('investment')}（${t('count')}）`,
    balanceAmountOfInvestment: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('investment')}（${t('amount')}）`,
    balanceNumberOfLoan: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('loan')}（${t('count')}）`,
    balanceAmountOfLoan: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('loan')}（${t('amount')}）`,
    balanceNumberOfDebtGuarantee: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('debt-guarantee')}（${t('count')}）`,
    balanceAmountOfDebtGuarantee: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('debt-guarantee')}（${t('amount')}）`,
    necessityType: `${t('necessity-of-fund-method')}（${t('fund-necessity-type')}）`,
    necessityReasons: `${t('necessity-of-fund-method')}（${t('report-local-government-fund-header-fund-necessity-reasons')}）`,
    projectDetailPageUrl: t('project-detail-page-url'),
  };
};

export const convertLocalGovernmentFundTableBToCsv = ({
  headers,
  reports,
  isDebugMode,
  projectDetailUrlType,
}: {
  headers: Record<
    z.infer<typeof reportLocalGovernmentFundTableBCsvHeader>,
    string
  >;
  reports: z.infer<typeof ReportLocalGovernmentFundsTableBModel>;
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}) => {
  const csvFormattedReports = reports.data.map((report) => {
    const values = valueConverter({
      data: report,
      type: 'csv',
      isDebugMode,
    });
    const projectDetailPageUrl = getProjectDetailPageUrl({
      isPS: projectDetailUrlType === 'public',
      projectId: report.id,
    });
    return {
      ...report,
      fundSheetNumber: `${report.fundSheetNumber}${
        report.fundSheetBranchNumber ? `-${report.fundSheetBranchNumber}` : ''
      }`,
      accountingTypeNames: report.accountingTypeNames.join('、'),
      necessityType: report.necessityType
        ? FundNecessityTypeLabelModifier[report.necessityType]
        : '',
      projectDetailPageUrl,
      beginningOfTheFundBalance: values.beginningOfTheFundBalance,
      nationalEquivalentOfBeginningOfTheFundBalance:
        values.nationalEquivalentOfBeginningOfTheFundBalance,
      incomeAmount: values.incomeAmount,
      nationalEquivalentOfIncomeAmount: values.nationalEquivalentOfIncomeAmount,
      grantedAmountByTheCountryFromInitial:
        values.grantedAmountByTheCountryFromInitial,
      grantedAmountByTheCountryFromSupplementary:
        values.grantedAmountByTheCountryFromSupplementary,
      grantedAmountByTheCountryFromReserve:
        values.grantedAmountByTheCountryFromReserve,
      otherIncomeAmount: values.otherIncomeAmount,
      businessExpense: values.businessExpense,
      amountReturnedToTheNationalTreasury:
        values.amountReturnedToTheNationalTreasury,
      endingOfTheFundBalance: values.endingOfTheFundBalance,
      nationalEquivalentOfTheEndOfTheFundBalance:
        values.nationalEquivalentOfTheEndOfTheFundBalance,
      decidedAmountOfSubsidy: values.decidedAmountOfSubsidy,
      decidedAmountOfInvestment: values.decidedAmountOfInvestment,
      decidedAmountOfLoan: values.decidedAmountOfLoan,
      decidedAmountOfDebtGuarantee: values.decidedAmountOfDebtGuarantee,
      decidedAmountOfOthers: values.decidedAmountOfOthers,
      balanceAmountOfInvestment: values.balanceAmountOfInvestment,
      balanceAmountOfLoan: values.balanceAmountOfLoan,
      balanceAmountOfDebtGuarantee: values.balanceAmountOfDebtGuarantee,
    };
  });

  const csvData = createCsv({
    headerKeys: reportLocalGovernmentFundTableBCsvHeader.options,
    headerConverter: headers,
    body: csvFormattedReports,
  });

  return csvData;
};

/// ////////////////////////////////////////
/// PDF
/// ////////////////////////////////////////

const LOCAL_GOVERNMENT_FUND_B_PDF_FONT_SIZE = 4.5;

const amountCellStyle: CSSProperties = {
  padding: 0.1,
};
const rowTextBaseStyle: CSSProperties = {
  fontSize: LOCAL_GOVERNMENT_FUND_B_PDF_FONT_SIZE,
  textAlign: 'center',
};
const cellTextBaseStyle = {
  fontSize: '6px',
  textAlign: 'center' as const,
};

const amountCellTextStyle = {
  ...cellTextBaseStyle,
  textAlign: 'right' as const,
};

const AMOUNT_CELL_RATIOS = '60px' as const;

const columnRatios = {
  // 同じレベルは合計で100になるようにする
  fundSheetNumber: 3,
  ministryName: 25,
  fundName: 26,
  beginningOfTheFundBalance: [AMOUNT_CELL_RATIOS, AMOUNT_CELL_RATIOS],
  incomeAndExpenditure: {
    income: [AMOUNT_CELL_RATIOS, '360px'],
    subsetNationalExpense: [AMOUNT_CELL_RATIOS, '300px'],
  },
  amountReturnedToTheNationalTreasury: AMOUNT_CELL_RATIOS,
  endingOfTheFundBalance: [AMOUNT_CELL_RATIOS, AMOUNT_CELL_RATIOS],
  decisionOnProjectImplementation: Array(5).fill(AMOUNT_CELL_RATIOS),
  loanBalance: Array(3).fill(AMOUNT_CELL_RATIOS),
  necessityType: 46,
};

export const getLocalGovernmentFundTableBHeader = (
  fiscalYear: number,
): ReportTableProps['header'] => {
  const reportYear = getReportTargetYear(fiscalYear);
  return [
    {
      type: 'text',
      value: t('report-local-government-fund-header-fund-sheet-number'),
      ratio: columnRatios.fundSheetNumber,
      textStyle: cellTextBaseStyle,
    },
    {
      type: 'text',
      value: t('ministry'),
      ratio: columnRatios.ministryName,
      textStyle: cellTextBaseStyle,
    },
    {
      type: 'text',
      value: t('fund-name'),
      ratio: columnRatios.fundName,
      textStyle: cellTextBaseStyle,
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'subset',
        cellRowRatios: [1, 2],
        cellColRatios: columnRatios.beginningOfTheFundBalance,
        containedCell: {
          type: 'text',
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          value: t(
            'report-local-government-fund-header-beginning-of-the-fund-balance',
            {
              year: reportYear - 1,
            },
          ),
          textStyle: cellTextBaseStyle,
        },
        includedCell: {
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          type: 'text',
          value: t(
            'report-local-government-fund-header-amount-equivalent-to-national-expense',
          ),
          textStyle: cellTextBaseStyle,
        },
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        vertical: true,
        cellRatios: ['1em', 1],
        flexCells: [
          {
            type: 'text',
            value: t(
              'report-local-government-fund-header-income-and-expenditure',
              { year: reportYear },
            ),
            textStyle: cellTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              flexCells: [
                {
                  type: 'multiple',
                  cell: {
                    type: 'subset',
                    cellRowRatios: ['1em', 1],
                    isContainedCellFullHeight: true,
                    cellColRatios: columnRatios.incomeAndExpenditure.income,
                    containedCell: {
                      type: 'text',
                      // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
                      textWrapperStyle: { height: '100%' },
                      value: t('report-local-government-fund-header-income'),
                      textStyle: cellTextBaseStyle,
                    },
                    includedCell: {
                      type: 'multiple',
                      cell: {
                        type: 'subset',
                        cellRowRatios: ['1em', 1],
                        isContainedCellFullHeight: true,
                        cellColRatios:
                          columnRatios.incomeAndExpenditure
                            .subsetNationalExpense,
                        containedCell: {
                          type: 'text',
                          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
                          textWrapperStyle: { height: '100%' },
                          value: t(
                            'report-local-government-fund-header-amount-equivalent-to-national-expense',
                          ),
                          textStyle: cellTextBaseStyle,
                        },
                        includedCell: {
                          type: 'multiple',
                          cell: {
                            type: 'flex',
                            flexCells: [
                              {
                                type: 'multiple',
                                cell: {
                                  type: 'flex',
                                  cellRatios: [1, 2],
                                  flexCells: [
                                    {
                                      ...centerTextStyle,
                                      value: t(
                                        'amount-of-funding-from-the-government',
                                      ),
                                      textStyle: cellTextBaseStyle,
                                    },
                                    {
                                      type: 'multiple',
                                      cell: {
                                        flexCells: [
                                          {
                                            value: t('initial'),
                                            textStyle: cellTextBaseStyle,
                                            wrapperStyle: {
                                              width: AMOUNT_CELL_RATIOS,
                                              display: 'flex',
                                            },
                                            textWrapperStyle: { width: '100%' },
                                          },
                                          {
                                            value: t('correction'),
                                            textStyle: cellTextBaseStyle,
                                            wrapperStyle: {
                                              width: AMOUNT_CELL_RATIOS,
                                              display: 'flex',
                                            },
                                            textWrapperStyle: { width: '100%' },
                                          },
                                          {
                                            value: t('reserve-budget'),
                                            textStyle: cellTextBaseStyle,
                                            wrapperStyle: {
                                              width: AMOUNT_CELL_RATIOS,
                                              display: 'flex',
                                            },
                                            textWrapperStyle: { width: '100%' },
                                          },
                                          {
                                            value: t('accounting-type'),
                                            textStyle: cellTextBaseStyle,
                                            wrapperStyle: {
                                              width: AMOUNT_CELL_RATIOS,
                                              display: 'flex',
                                            },
                                            textWrapperStyle: { width: '100%' },
                                          },
                                        ],
                                      },
                                    },
                                  ],
                                  vertical: true,
                                },
                              },
                              {
                                value: t('others'),
                                textStyle: cellTextBaseStyle,
                                wrapperStyle: {
                                  width: AMOUNT_CELL_RATIOS,
                                  display: 'flex',
                                },
                                textWrapperStyle: { width: '100%' },
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                },
                {
                  align: 'center',
                  value: t('report-local-government-fund-header-expense'),
                  textStyle: cellTextBaseStyle,
                  wrapperStyle: {
                    width: AMOUNT_CELL_RATIOS,
                    display: 'flex',
                  },
                  textWrapperStyle: { width: '100%' },
                },
              ],
            },
          },
        ],
      },
    },
    {
      type: 'text',
      value: t(
        'report-local-government-fund-header-amount-of-national-treasury-return',
        { year: reportYear },
      ),
      ratio: 'auto',
      textStyle: cellTextBaseStyle,
      textWrapperStyle: {
        width: columnRatios.amountReturnedToTheNationalTreasury,
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'subset',
        cellRowRatios: [1, 2],
        cellColRatios: columnRatios.endingOfTheFundBalance,
        containedCell: {
          type: 'text',
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          value: t(
            'report-local-government-fund-header-ending-of-the-fund-balance',
            {
              year: reportYear,
            },
          ),
          textStyle: cellTextBaseStyle,
        },
        includedCell: {
          // headerは固定幅なのでheightを100%にしてもレイアウト崩れは起きない
          textWrapperStyle: { height: '100%' },
          type: 'text',
          value: t(
            'report-local-government-fund-header-amount-equivalent-to-national-expense',
          ),
          textStyle: cellTextBaseStyle,
        },
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        vertical: true,
        cellRatios: ['1em', 1],
        flexCells: [
          {
            ...centerTextStyle,
            value: t(
              'report-local-government-fund-header-decision-on-project-implementation',
              { year: reportYear },
            ),
            textStyle: cellTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              cellRatios: columnRatios.decisionOnProjectImplementation,
              flexCells: [
                `${t('fund-business-subsidy')}\n${t('report-local-government-fund-header-supplementary-for-local-government-fund-business')}`,
                t('investment'),
                t('loan'),
                t('debt-guarantee'),
                t('fund-business-for-local-government-others'),
              ].map((item) => ({
                type: 'multiple',
                cell: {
                  type: 'flex',
                  vertical: true,
                  borderStyle: 'dotted',
                  flexCells: [
                    {
                      ...centerTextStyle,
                      value: item,
                      textStyle: cellTextBaseStyle,
                    },
                    {
                      ...centerTextStyle,
                      value: `（${t('count')}）\n${t('amount')}`,
                      textStyle: cellTextBaseStyle,
                    },
                  ],
                },
              })),
            },
          },
        ],
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        vertical: true,
        cellRatios: ['1em', 1],
        flexCells: [
          {
            ...centerTextStyle,
            value: t('report-local-government-fund-header-loan-balance', {
              year: reportYear,
            }),
            textStyle: cellTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              cellRatios: columnRatios.loanBalance,
              flexCells: [t('investment'), t('loan'), t('debt-guarantee')].map(
                (item) => ({
                  type: 'multiple',
                  cell: {
                    type: 'flex',
                    vertical: true,
                    borderStyle: 'dotted',
                    flexCells: [
                      {
                        ...centerTextStyle,
                        value: item,
                        textStyle: cellTextBaseStyle,
                      },
                      {
                        ...centerTextStyle,
                        value: `（${t('count')}）\n${t('amount')}`,
                        textStyle: cellTextBaseStyle,
                      },
                    ],
                  },
                }),
              ),
            },
          },
        ],
      },
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            type: 'text',
            value: t('necessity-of-fund-method'),
            textStyle: cellTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              vertical: true,
              borderStyle: 'dotted',
              flexCells: [
                {
                  ...centerTextStyle,
                  value: t('fund-necessity-type'),
                  textStyle: cellTextBaseStyle,
                },
                {
                  ...centerTextStyle,
                  value: t(
                    'report-local-government-fund-header-fund-necessity-reasons',
                  ),
                  textStyle: cellTextBaseStyle,
                },
              ],
            },
          },
        ],
        vertical: true,
        cellRatios: ['1em', 1],
      },
      ratio: columnRatios.necessityType,
    },
  ];
};

const getCountAmountText = ({
  count,
  amount,
}: {
  count: number | null;
  amount: number | string;
}) => {
  return `（${formatNumber(count)}）\n${amount}`;
};

const incomeAndExpenditureCellRatios = [
  AMOUNT_CELL_RATIOS,
  '61px',
  '61px',
  AMOUNT_CELL_RATIOS,
  AMOUNT_CELL_RATIOS,
  AMOUNT_CELL_RATIOS,
  '58px',
  AMOUNT_CELL_RATIOS,
];

const getLocalGovernmentFundTableBRow = ({
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  data: z.infer<typeof ReportLocalGovernmentFundTableBModel>;
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableCell[] => {
  const values = valueConverter({
    data,
    type: 'pdf',
    isDebugMode,
  });
  return [
    {
      type: 'text',
      value: `${data.fundSheetNumber.toString()}${
        data.fundSheetBranchNumber ? `-${data.fundSheetBranchNumber}` : ''
      }`,
      textStyle: cellTextBaseStyle,
    },
    {
      type: 'text',
      value: data.ministryName,
      textStyle: cellTextBaseStyle,
    },
    {
      type: 'link',
      href: getProjectDetailPageUrl({
        isPS: projectDetailUrlType === 'public',
        projectId: data.id,
      }),
      value: data.projectName,
      textStyle: cellTextBaseStyle,
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            ...centerTextStyle,
            value: values.beginningOfTheFundBalance,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: values.nationalEquivalentOfBeginningOfTheFundBalance,
            textStyle: amountCellTextStyle,
          },
        ],
      },
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        cellRatios: incomeAndExpenditureCellRatios,
        flexCells: [
          {
            ...centerTextStyle,
            value: values.incomeAmount,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: values.nationalEquivalentOfIncomeAmount,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: values.grantedAmountByTheCountryFromInitial,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: values.grantedAmountByTheCountryFromSupplementary,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: values.grantedAmountByTheCountryFromReserve,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: data.accountingTypeNames.join(', ') || FALLBACK_TEXT,
            textStyle: cellTextBaseStyle,
          },
          {
            ...centerTextStyle,
            value: values.otherIncomeAmount,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: values.businessExpense,
            textStyle: amountCellTextStyle,
          },
        ],
      },
    },
    {
      type: 'text',
      value: values.amountReturnedToTheNationalTreasury,
      wrapperStyle: amountCellStyle,
      textStyle: amountCellTextStyle,
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            ...centerTextStyle,
            value: values.endingOfTheFundBalance,
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: values.nationalEquivalentOfTheEndOfTheFundBalance,
            textStyle: amountCellTextStyle,
          },
        ],
      },
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.decidedNumberOfSubsidy,
              amount: values.decidedAmountOfSubsidy,
            }),
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.decidedNumberOfInvestment,
              amount: values.decidedAmountOfInvestment,
            }),
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.decidedNumberOfLoan,
              amount: values.decidedAmountOfLoan,
            }),
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.decidedNumberOfDebtGuarantee,
              amount: values.decidedAmountOfDebtGuarantee,
            }),
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.decidedNumberOfOthers,
              amount: values.decidedAmountOfOthers,
            }),
            textStyle: amountCellTextStyle,
          },
        ],
      },
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        flexCells: [
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.balanceNumberOfInvestment,
              amount: values.balanceAmountOfInvestment,
            }),
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.balanceNumberOfLoan,
              amount: values.balanceAmountOfLoan,
            }),
            textStyle: amountCellTextStyle,
          },
          {
            ...centerTextStyle,
            value: getCountAmountText({
              count: data.balanceNumberOfDebtGuarantee,
              amount: values.balanceAmountOfDebtGuarantee,
            }),
            textStyle: amountCellTextStyle,
          },
        ],
      },
    },
    {
      type: 'text',
      value: `${data.necessityType ? FundNecessityTypeLabelModifier[data.necessityType] : FALLBACK_TEXT}\n${data.necessityReasons ?? FALLBACK_TEXT}`,
      textStyle: { ...cellTextBaseStyle, textAlign: 'left' },
    },
  ];
};

export const getLocalGovernmentFundTableBRowData = ({
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  data: z.infer<typeof ReportLocalGovernmentFundsTableBModel.shape.data>;
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableProps['data'] => {
  return data.map((d) => ({
    row: getLocalGovernmentFundTableBRow({
      data: d,
      isDebugMode,
      projectDetailUrlType,
    }),
    rowStyle: {
      breakInside: 'avoid',
    },
  }));
};

export const getLocalGovernmentFundTableBFooter = (
  summaryData: z.infer<
    typeof ReportLocalGovernmentFundsTableBModel.shape.summaryData
  >,
  isDebugMode: boolean,
): ReportTableProps['footer'] => {
  return [
    {
      row: [
        {
          type: 'text',
          value: t('total'),
          colSpan: 3,
          textStyle: cellTextBaseStyle,
        },
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  summaryData.beginningOfTheFundBalance,
                  {
                    rawValue: summaryData.rawBeginningOfTheFundBalance,
                    isDebugMode,
                  },
                ),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  summaryData.nationalEquivalentOfBeginningOfTheFundBalance,
                  {
                    rawValue:
                      summaryData.rawNationalEquivalentOfBeginningOfTheFundBalance,
                    isDebugMode,
                  },
                ),
                textStyle: amountCellTextStyle,
              },
            ],
          },
        },
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            cellRatios: incomeAndExpenditureCellRatios,
            flexCells: [
              {
                ...centerTextStyle,
                value: formatAmountForPdf(summaryData.incomeAmount, {
                  rawValue: summaryData.rawIncomeAmount,
                  isDebugMode,
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  summaryData.nationalEquivalentOfIncomeAmount,
                  {
                    rawValue: summaryData.rawNationalEquivalentOfIncomeAmount,
                    isDebugMode,
                  },
                ),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  summaryData.grantedAmountByTheCountryFromInitial,
                  {
                    rawValue:
                      summaryData.rawGrantedAmountByTheCountryFromInitial,
                    isDebugMode,
                  },
                ),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  summaryData.grantedAmountByTheCountryFromSupplementary,
                  {
                    rawValue:
                      summaryData.rawGrantedAmountByTheCountryFromSupplementary,
                    isDebugMode,
                  },
                ),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  summaryData.grantedAmountByTheCountryFromReserve,
                  {
                    rawValue:
                      summaryData.rawGrantedAmountByTheCountryFromReserve,
                    isDebugMode,
                  },
                ),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: '',
                textStyle: rowTextBaseStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(summaryData.otherIncomeAmount, {
                  rawValue: summaryData.rawOtherIncomeAmount,
                  isDebugMode,
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(summaryData.businessExpense, {
                  rawValue: summaryData.rawBusinessExpense,
                  isDebugMode,
                }),
                textStyle: amountCellTextStyle,
              },
            ],
          },
        },
        {
          type: 'text',
          value: formatAmountForPdf(
            summaryData.amountReturnedToTheNationalTreasury,
            {
              rawValue: summaryData.rawAmountReturnedToTheNationalTreasury,
              isDebugMode,
            },
          ),
          textStyle: amountCellTextStyle,
        },
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                ...centerTextStyle,
                value: formatAmountForPdf(summaryData.endingOfTheFundBalance, {
                  rawValue: summaryData.rawEndingOfTheFundBalance,
                  isDebugMode,
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: formatAmountForPdf(
                  summaryData.nationalEquivalentOfTheEndOfTheFundBalance,
                  {
                    rawValue:
                      summaryData.rawNationalEquivalentOfTheEndOfTheFundBalance,
                    isDebugMode,
                  },
                ),
                textStyle: amountCellTextStyle,
              },
            ],
          },
        },
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.decidedNumberOfSubsidy,
                  amount: formatAmountForPdf(
                    summaryData.decidedAmountOfSubsidy,
                    {
                      rawValue: summaryData.rawDecidedAmountOfSubsidy,
                      isDebugMode,
                    },
                  ),
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.decidedNumberOfInvestment,
                  amount: formatAmountForPdf(
                    summaryData.decidedAmountOfInvestment,
                    {
                      rawValue: summaryData.rawDecidedAmountOfInvestment,
                      isDebugMode,
                    },
                  ),
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.decidedNumberOfLoan,
                  amount: formatAmountForPdf(summaryData.decidedAmountOfLoan, {
                    rawValue: summaryData.rawDecidedAmountOfLoan,
                    isDebugMode,
                  }),
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.decidedNumberOfDebtGuarantee,
                  amount: formatAmountForPdf(
                    summaryData.decidedAmountOfDebtGuarantee,
                    {
                      rawValue: summaryData.rawDecidedAmountOfDebtGuarantee,
                      isDebugMode,
                    },
                  ),
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.decidedNumberOfOthers,
                  amount: formatAmountForPdf(
                    summaryData.decidedAmountOfOthers,
                    {
                      rawValue: summaryData.rawDecidedAmountOfOthers,
                      isDebugMode,
                    },
                  ),
                }),
                textStyle: amountCellTextStyle,
              },
            ],
          },
        },
        {
          type: 'multiple',
          cell: {
            type: 'flex',
            flexCells: [
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.balanceNumberOfInvestment,
                  amount: formatAmountForPdf(
                    summaryData.balanceAmountOfInvestment,
                    {
                      rawValue: summaryData.rawBalanceAmountOfInvestment,
                      isDebugMode,
                    },
                  ),
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.balanceNumberOfLoan,
                  amount: formatAmountForPdf(summaryData.balanceAmountOfLoan, {
                    rawValue: summaryData.rawBalanceAmountOfLoan,
                    isDebugMode,
                  }),
                }),
                textStyle: amountCellTextStyle,
              },
              {
                ...centerTextStyle,
                value: getCountAmountText({
                  count: summaryData.balanceNumberOfDebtGuarantee,
                  amount: formatAmountForPdf(
                    summaryData.balanceAmountOfDebtGuarantee,
                    {
                      rawValue: summaryData.rawBalanceAmountOfDebtGuarantee,
                      isDebugMode,
                    },
                  ),
                }),
                textStyle: amountCellTextStyle,
              },
            ],
          },
        },
        {
          type: 'text',
          value: '',
        },
      ],
    },
  ];
};
